import {exhaustive} from '@freckle/exhaustive'
import type {ParserT} from '@freckle/parser'
import {stringEnum} from '@freckle/parser'

export type MathAssignmentTypeT =
  | 'MathAdaptive'
  | 'MathAssessment'
  | 'MathDepthOfKnowledgePractice'
  | 'MathFactPractice'
  | 'MathFocusSkillsPractice'
  | 'MathIbl'
  | 'MathNumberFacts'
  | 'MathStandard'
  | 'MathTargetedDepthOfKnowledgePractice'

type AllMathAssignmentsOptionT = 'all'
export const AllMathAssignmentsOption: AllMathAssignmentsOptionT = 'all'

export type MathAssignmentOptionT = MathAssignmentTypeT | AllMathAssignmentsOptionT

type MathAssignmentTypesT = {
  MathAdaptive: MathAssignmentTypeT
  MathAssessment: MathAssignmentTypeT
  MathDepthOfKnowledgePractice: MathAssignmentTypeT
  MathFactPractice: MathAssignmentTypeT
  MathFocusSkillsPractice: MathAssignmentTypeT
  MathIbl: MathAssignmentTypeT
  MathNumberFacts: MathAssignmentTypeT
  MathStandard: MathAssignmentTypeT
  MathTargetedDepthOfKnowledgePractice: MathAssignmentTypeT
  all: () => Array<MathAssignmentTypeT>
  forDisplay: (mathAssignmentType: MathAssignmentTypeT) => string
  parse: (text: string) => MathAssignmentTypeT | undefined | null
}

export const MathAssignmentTypes: MathAssignmentTypesT = {
  MathAdaptive: 'MathAdaptive',
  MathAssessment: 'MathAssessment',
  MathDepthOfKnowledgePractice: 'MathDepthOfKnowledgePractice',
  MathFactPractice: 'MathFactPractice',
  MathFocusSkillsPractice: 'MathFocusSkillsPractice',
  MathIbl: 'MathIbl',
  MathNumberFacts: 'MathNumberFacts',
  MathStandard: 'MathStandard',
  MathTargetedDepthOfKnowledgePractice: 'MathTargetedDepthOfKnowledgePractice',
  all: function (): Array<MathAssignmentTypeT> {
    return [
      MathAssignmentTypes.MathAdaptive,
      MathAssignmentTypes.MathAssessment,
      MathAssignmentTypes.MathDepthOfKnowledgePractice,
      MathAssignmentTypes.MathFactPractice,
      MathAssignmentTypes.MathFocusSkillsPractice,
      MathAssignmentTypes.MathIbl,
      MathAssignmentTypes.MathNumberFacts,
      MathAssignmentTypes.MathStandard,
      MathAssignmentTypes.MathTargetedDepthOfKnowledgePractice
    ]
  },
  forDisplay: function (assignmentType: MathAssignmentTypeT): string {
    switch (assignmentType) {
      case 'MathAdaptive':
        return 'math-adaptive'
      case 'MathAssessment':
        return 'math-assessment'
      case 'MathDepthOfKnowledgePractice':
        return 'math-depth-of-knowledge-practice'
      case 'MathFactPractice':
        return 'math-fact-practice'
      case 'MathFocusSkillsPractice':
        return 'math-focus-skills-practice'
      case 'MathIbl':
        return 'math-ibl'
      case 'MathNumberFacts':
        return 'math-number-facts'
      case 'MathStandard':
        return 'math-standard'
      case 'MathTargetedDepthOfKnowledgePractice':
        return 'math-targeted-depth-of-knowledge-practice'
      default:
        return exhaustive(assignmentType, 'MathAssignmentTypeT')
    }
  },
  parse: function (text: string): MathAssignmentTypeT | undefined | null {
    const trimmed = text.trim()
    return MathAssignmentTypes.all().find(assignmentType => assignmentType === trimmed)
  }
}

export const parse: ParserT<MathAssignmentTypeT> = stringEnum(
  'MathAssignmentTypeT',
  MathAssignmentTypes.parse
)
